import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import useApi from '../api';
import { OrganizationCombobox, OrganizationOption } from './OrganizationCombobox';
import CreditPlansSection from './CreditPlansSection';

import { TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { LoadingState } from "@/components/ui/LoadingState";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

// ---------------------------------------------------------------------------
// Types (mirror backend/api/routes/metering_admin.py responses)
// ---------------------------------------------------------------------------

interface MeterRow {
    meter: string;
    cost_usd: number;
    credits: number;
    quantity: number;
    charge_count: number;
}

interface MeteringSummary {
    time_range: string;
    totals: { cost_usd: number; credits: number; quantity: number; charge_count: number };
    by_meter: MeterRow[];
    trend: { date: string; cost_usd: number; credits: number }[];
    top_organizations: {
        organization_id: string;
        organization_name: string;
        cost_usd: number;
        credits: number;
        charge_count: number;
    }[];
    active_rate_card_version: number | null;
    credit_usd: number;
}

interface ChargeRow {
    id: string;
    organization_id: string;
    organization_name: string;
    occurred_at: string | null;
    meter: string;
    quantity: number;
    unit: string;
    cost_usd: number;
    credits: number;
    rate_card_version: number;
    backfilled: boolean;
    metadata: Record<string, any>;
}

interface RateEntry {
    multiplier?: number;
    unit_cost_usd?: number | null;
    unit?: string;
}

// What a known meter actually prices at on a card, aliases and defaults
// resolved server-side (metering_service.effective_rates).
interface EffectiveRateEntry extends RateEntry {
    source: 'card' | 'alias' | 'default' | 'default_multiplier';
    alias_of?: string;
}

type DraftMeter = { multiplier: string; unit_cost_usd: string; unit?: string; inherited?: string };

interface RateCardData {
    id: string;
    version: number;
    status: 'draft' | 'active' | 'retired';
    credit_usd: number;
    rates: { default_multiplier?: number; meters?: Record<string, RateEntry> };
    effective_meters?: Record<string, EffectiveRateEntry>;
    notes: string | null;
    activated_at: string | null;
    created_at: string | null;
}

interface SimulationResult {
    days: number;
    by_meter: {
        meter: string;
        quantity: number;
        cost_usd: number;
        actual_credits: number;
        simulated_credits: number;
    }[];
    totals: { cost_usd: number; actual_credits: number; simulated_credits: number };
}

interface DiffChange {
    meter: string;
    old_multiplier: number;
    new_multiplier: number;
    old_unit_cost_usd: number | null;
    new_unit_cost_usd: number | null;
    multiplier_change_pct?: number;
}

// ---------------------------------------------------------------------------
// Meter display taxonomy: category -> subtype straight from the dotted name
// ---------------------------------------------------------------------------

const METER_LABELS: Record<string, string> = {
    'ai.chat': 'Agent (interactive)',
    'ai.run.job': 'Jobs',
    'sandbox.compute': 'Job compute',
    'ai.run.service': 'Services (legacy, now ai.run.job)',
    'ai.run.proactive': 'Proactive engagement',
    'ai.run.research': 'Account research',
    'ai.run.summary': 'Visitor summaries',
    'ai.run.outcome': 'Outcome classification',
    'ai.run.question': 'Question extraction',
    'ai.run.moderation_scan': 'Session moderation scan',
    'ai.run.moderation_review': 'Session moderation review',
    'ai.embedding': 'Embeddings',
    'voice.tts': 'Text-to-speech',
    'voice.stt': 'Speech-to-text',
    'identification.rb2b': 'RB2B resolutions',
    'enrichment.pdl': 'Company enrichments',
    'email.outbound': 'Outbound email',
    'crawl.page': 'Crawled pages',
};

const GROUP_LABELS: Record<string, string> = {
    ai: 'AI',
    voice: 'Voice',
    identification: 'Identification',
    enrichment: 'Enrichment',
    email: 'Email',
    crawl: 'Crawling',
};

const meterGroup = (meter: string) => meter.split('.')[0];
const meterLabel = (meter: string) => METER_LABELS[meter] || meter;
const groupLabel = (group: string) => GROUP_LABELS[group] || group;

const fmtUsd = (value: number) =>
    `$${value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: value < 1 ? 4 : 2,
    })}`;
const fmtCredits = (value: number) =>
    value.toLocaleString(undefined, { maximumFractionDigits: value < 10 ? 2 : 0 });
const fmtQuantity = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 0 });

const MeteringTab: React.FC = () => {
    const { get, post, put } = useApi();

    // Usage readouts
    const [timeRange, setTimeRange] = useState('30d');
    const [organizationId, setOrganizationId] = useState('all');
    const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);
    const [summary, setSummary] = useState<MeteringSummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [trendView, setTrendView] = useState<'cost' | 'credits'>('cost');

    // Ledger drill-down
    const [charges, setCharges] = useState<ChargeRow[]>([]);
    const [chargesTotalPages, setChargesTotalPages] = useState(1);
    const [chargesPage, setChargesPage] = useState(1);
    const [chargesMeter, setChargesMeter] = useState('all');

    // Rate cards
    const [rateCards, setRateCards] = useState<RateCardData[]>([]);
    const [knownMeters, setKnownMeters] = useState<string[]>([]);
    // canonical meter -> legacy name whose entry still prices it on older cards
    const [meterAliases, setMeterAliases] = useState<Record<string, string>>({});
    const [editingCardId, setEditingCardId] = useState<string | null>(null);
    const [draftMeters, setDraftMeters] = useState<Record<string, DraftMeter>>({});
    const [draftDefaultMultiplier, setDraftDefaultMultiplier] = useState('3.33');
    const [draftNotes, setDraftNotes] = useState('');
    const [savingDraft, setSavingDraft] = useState(false);
    const [simulation, setSimulation] = useState<SimulationResult | null>(null);
    const [simulating, setSimulating] = useState(false);
    const [confirmChanges, setConfirmChanges] = useState<DiffChange[] | null>(null);
    const [activating, setActivating] = useState(false);

    const editingCard = rateCards.find((card) => card.id === editingCardId) || null;
    const activeCard = rateCards.find((card) => card.status === 'active') || null;

    const fetchSummary = useCallback(async () => {
        setSummaryLoading(true);
        try {
            const params = new URLSearchParams({ time_range: timeRange });
            if (organizationId !== 'all') params.set('organization_id', organizationId);
            const response = await get(`/admin/metering/summary?${params.toString()}`);
            setSummary(response.data);
        } catch (error) {
            console.error('Error fetching metering summary:', error);
            toast.error('Failed to load metering summary');
        } finally {
            setSummaryLoading(false);
        }
    }, [get, timeRange, organizationId]);

    const fetchCharges = useCallback(async () => {
        try {
            const params = new URLSearchParams({ page: String(chargesPage), per_page: '25' });
            if (organizationId !== 'all') params.set('organization_id', organizationId);
            if (chargesMeter !== 'all') params.set('meter', chargesMeter);
            const response = await get(`/admin/metering/charges?${params.toString()}`);
            setCharges(response.data.charges || []);
            setChargesTotalPages(response.data.total_pages || 1);
        } catch (error) {
            console.error('Error fetching charges:', error);
        }
    }, [get, chargesPage, chargesMeter, organizationId]);

    const fetchRateCards = useCallback(async () => {
        try {
            const response = await get('/admin/metering/rate-cards');
            setRateCards(response.data.rate_cards || []);
            setKnownMeters(response.data.known_meters || []);
            setMeterAliases(response.data.meter_aliases || {});
        } catch (error) {
            console.error('Error fetching rate cards:', error);
        }
    }, [get]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    useEffect(() => {
        fetchCharges();
    }, [fetchCharges]);

    useEffect(() => {
        fetchRateCards();
        get('/admin/organizations/')
            .then((response: any) =>
                setOrganizations(
                    (response.data || []).map((org: any) => ({ id: org.id, name: org.name }))
                )
            )
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Grouped by-meter rows: category header + its meters, ordered by spend.
    const groupedMeters = useMemo(() => {
        const rows = summary?.by_meter || [];
        const groups = new Map<string, MeterRow[]>();
        for (const row of rows) {
            const key = meterGroup(row.meter);
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key)!.push(row);
        }
        return Array.from(groups.entries())
            .map(([group, members]) => ({
                group,
                members,
                totals: members.reduce(
                    (acc, member) => ({
                        cost_usd: acc.cost_usd + member.cost_usd,
                        credits: acc.credits + member.credits,
                        charge_count: acc.charge_count + member.charge_count,
                    }),
                    { cost_usd: 0, credits: 0, charge_count: 0 }
                ),
            }))
            .sort((a, b) => b.totals.cost_usd - a.totals.cost_usd);
    }, [summary]);

    // -----------------------------------------------------------------------
    // Rate-card draft editing
    // -----------------------------------------------------------------------

    // The meters a card is edited as: every known meter plus the card's own
    // extras, with a legacy name (e.g. ai.run.service) folded into its
    // canonical meter (ai.run.job) rather than carried forward as a second row.
    const editableMeterNames = (card: RateCardData) => {
        const legacyNames = new Set(Object.values(meterAliases));
        return Array.from(new Set([...knownMeters, ...Object.keys(card.rates?.meters || {})])).filter(
            (name) => !legacyNames.has(name),
        );
    };

    // A card's rates with aliases and defaults resolved into explicit entries,
    // so a draft cloned from it prices every known meter by its current name.
    const canonicalRates = (card: RateCardData) => {
        const source = card.rates?.meters || {};
        const effective = card.effective_meters || {};
        const meters: Record<string, RateEntry> = {};
        for (const name of editableMeterNames(card)) {
            const entry = source[name] || effective[name];
            if (!entry) continue;
            const { source: _source, alias_of: _aliasOf, ...rate } = entry as EffectiveRateEntry;
            meters[name] = rate;
        }
        return { default_multiplier: card.rates?.default_multiplier ?? 3.33, meters };
    };

    const beginEditing = (card: RateCardData) => {
        setEditingCardId(card.id);
        setSimulation(null);
        const meters: Record<string, DraftMeter> = {};
        const source = card.rates?.meters || {};
        const effective = card.effective_meters || {};
        for (const name of editableMeterNames(card)) {
            // Seed a meter the card predates from what it effectively prices at
            // today (its alias or the built-in default) — a blank input would be
            // saved as multiplier 0, i.e. the meter would silently become free.
            const resolved = effective[name];
            const entry: RateEntry = source[name] || resolved || {};
            const inherited =
                !source[name] && resolved
                    ? resolved.source === 'alias'
                        ? `inherited from ${resolved.alias_of}`
                        : resolved.source === 'default'
                          ? 'built-in default'
                          : 'default multiplier'
                    : undefined;
            meters[name] = {
                multiplier: entry.multiplier !== undefined ? String(entry.multiplier) : '',
                unit_cost_usd:
                    entry.unit_cost_usd !== undefined && entry.unit_cost_usd !== null
                        ? String(entry.unit_cost_usd)
                        : '',
                unit: entry.unit,
                inherited,
            };
        }
        setDraftMeters(meters);
        setDraftDefaultMultiplier(String(card.rates?.default_multiplier ?? 3.33));
        setDraftNotes(card.notes || '');
    };

    const buildRatesPayload = () => {
        const meters: Record<string, RateEntry> = {};
        for (const [name, entry] of Object.entries(draftMeters)) {
            const rate: RateEntry = { multiplier: parseFloat(entry.multiplier) || 0 };
            if (entry.unit_cost_usd.trim() !== '') rate.unit_cost_usd = parseFloat(entry.unit_cost_usd) || 0;
            if (entry.unit) rate.unit = entry.unit;
            meters[name] = rate;
        }
        return { default_multiplier: parseFloat(draftDefaultMultiplier) || 0, meters };
    };

    const createDraft = async () => {
        const source = activeCard;
        if (!source) return;
        try {
            const response = await post('/admin/metering/rate-cards', {
                rates: canonicalRates(source),
                notes: `Draft from v${source.version}`,
            });
            await fetchRateCards();
            beginEditing(response.data);
            toast.success(`Draft v${response.data.version} created`);
        } catch (error: any) {
            toast.error(error?.detail || 'Failed to create draft');
        }
    };

    const saveDraft = async (): Promise<boolean> => {
        if (!editingCard) return false;
        setSavingDraft(true);
        try {
            await put(`/admin/metering/rate-cards/${editingCard.id}`, {
                rates: buildRatesPayload(),
                notes: draftNotes || null,
            });
            await fetchRateCards();
            return true;
        } catch (error: any) {
            toast.error(typeof error?.detail === 'string' ? error.detail : 'Failed to save draft');
            return false;
        } finally {
            setSavingDraft(false);
        }
    };

    const runSimulation = async () => {
        setSimulating(true);
        try {
            const payload: any = { rates: buildRatesPayload(), days: 30 };
            if (organizationId !== 'all') payload.organization_id = organizationId;
            const response = await post('/admin/metering/rate-cards/simulate', payload);
            setSimulation(response.data);
        } catch (error: any) {
            toast.error(typeof error?.detail === 'string' ? error.detail : 'Simulation failed');
        } finally {
            setSimulating(false);
        }
    };

    const activate = async (confirm: boolean) => {
        if (!editingCard) return;
        setActivating(true);
        try {
            if (!confirm) {
                const saved = await saveDraft();
                if (!saved) return;
            }
            await post(`/admin/metering/rate-cards/${editingCard.id}/activate`, { confirm });
            setConfirmChanges(null);
            setEditingCardId(null);
            setSimulation(null);
            await Promise.all([fetchRateCards(), fetchSummary()]);
            toast.success('Rate card activated');
        } catch (error: any) {
            const detail = error?.detail;
            if (detail && typeof detail === 'object' && Array.isArray(detail.changes)) {
                setConfirmChanges(detail.changes);
            } else {
                toast.error(typeof detail === 'string' ? detail : 'Activation failed');
            }
        } finally {
            setActivating(false);
        }
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    if (summaryLoading && !summary) {
        return (
            <TabsContent value="metering">
                <LoadingState />
            </TabsContent>
        );
    }

    const totals = summary?.totals || { cost_usd: 0, credits: 0, quantity: 0, charge_count: 0 };

    return (
        <TabsContent value="metering" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                <OrganizationCombobox
                    organizations={organizations}
                    value={organizationId}
                    onChange={(next) => {
                        setOrganizationId(next);
                        setChargesPage(1);
                    }}
                    triggerClassName="w-64"
                />
                <Select value={timeRange} onValueChange={setTimeRange}>
                    <SelectTrigger className="w-28">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="24h">24 hours</SelectItem>
                        <SelectItem value="7d">7 days</SelectItem>
                        <SelectItem value="30d">30 days</SelectItem>
                        <SelectItem value="90d">90 days</SelectItem>
                        <SelectItem value="365d">1 year</SelectItem>
                    </SelectContent>
                </Select>
                <Badge variant="outline" className="ml-auto">
                    Shadow metering — internal only
                </Badge>
            </div>

            {/* Totals */}
            <div className="grid gap-4 md:grid-cols-4">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">COGS</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{fmtUsd(totals.cost_usd)}</div>
                        <p className="text-xs text-muted-foreground">our variable cost, all meters</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Credits burned</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{fmtCredits(totals.credits)}</div>
                        <p className="text-xs text-muted-foreground">
                            ≈ {fmtUsd(totals.credits * (summary?.credit_usd || 0.01))} at list
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Charges</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{fmtQuantity(totals.charge_count)}</div>
                        <p className="text-xs text-muted-foreground">ledger rows in period</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Rate card</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {summary?.active_rate_card_version ? `v${summary.active_rate_card_version}` : '—'}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            1 credit = {fmtUsd(summary?.credit_usd || 0.01)} list
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Trend */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">
                        {trendView === 'cost' ? 'Daily COGS' : 'Daily credits'}
                    </CardTitle>
                    <div className="flex gap-1">
                        <Button
                            size="sm"
                            variant={trendView === 'cost' ? 'default' : 'outline'}
                            onClick={() => setTrendView('cost')}
                        >
                            COGS $
                        </Button>
                        <Button
                            size="sm"
                            variant={trendView === 'credits' ? 'default' : 'outline'}
                            onClick={() => setTrendView('credits')}
                        >
                            Credits
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={summary?.trend || []}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" fontSize={11} tickLine={false} axisLine={false} />
                            <YAxis fontSize={11} tickLine={false} axisLine={false} width={70} />
                            <Tooltip
                                formatter={(value: any) =>
                                    trendView === 'cost' ? fmtUsd(Number(value)) : fmtCredits(Number(value))
                                }
                            />
                            <Area
                                type="monotone"
                                dataKey={trendView === 'cost' ? 'cost_usd' : 'credits'}
                                stroke="hsl(var(--primary))"
                                fill="hsl(var(--primary))"
                                fillOpacity={0.15}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>

            <div className="grid gap-4 lg:grid-cols-2">
                {/* By meter, grouped category -> subtype */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Consumption by meter</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Meter</TableHead>
                                    <TableHead className="text-right">Charges</TableHead>
                                    <TableHead className="text-right">COGS</TableHead>
                                    <TableHead className="text-right">Credits</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {groupedMeters.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} className="text-center text-muted-foreground">
                                            No charges in this period
                                        </TableCell>
                                    </TableRow>
                                )}
                                {groupedMeters.map(({ group, members, totals: groupTotals }) => (
                                    <React.Fragment key={group}>
                                        <TableRow className="bg-muted/50 hover:bg-muted/50">
                                            <TableCell className="font-semibold">{groupLabel(group)}</TableCell>
                                            <TableCell className="text-right font-semibold">
                                                {fmtQuantity(groupTotals.charge_count)}
                                            </TableCell>
                                            <TableCell className="text-right font-semibold">
                                                {fmtUsd(groupTotals.cost_usd)}
                                            </TableCell>
                                            <TableCell className="text-right font-semibold">
                                                {fmtCredits(groupTotals.credits)}
                                            </TableCell>
                                        </TableRow>
                                        {members.map((row) => (
                                            <TableRow key={row.meter}>
                                                <TableCell className="pl-6 text-muted-foreground">
                                                    {meterLabel(row.meter)}
                                                    <span className="ml-2 text-xs opacity-60">{row.meter}</span>
                                                </TableCell>
                                                <TableCell className="text-right">{fmtQuantity(row.charge_count)}</TableCell>
                                                <TableCell className="text-right">{fmtUsd(row.cost_usd)}</TableCell>
                                                <TableCell className="text-right">{fmtCredits(row.credits)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* Top organizations */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">
                            {organizationId === 'all' ? 'Top organizations' : 'Organization detail'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {organizationId === 'all' ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Organization</TableHead>
                                        <TableHead className="text-right">Charges</TableHead>
                                        <TableHead className="text-right">COGS</TableHead>
                                        <TableHead className="text-right">Credits</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(summary?.top_organizations || []).map((org) => (
                                        <TableRow
                                            key={org.organization_id}
                                            className="cursor-pointer"
                                            onClick={() => setOrganizationId(org.organization_id)}
                                        >
                                            <TableCell>{org.organization_name}</TableCell>
                                            <TableCell className="text-right">{fmtQuantity(org.charge_count)}</TableCell>
                                            <TableCell className="text-right">{fmtUsd(org.cost_usd)}</TableCell>
                                            <TableCell className="text-right">{fmtCredits(org.credits)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {(summary?.top_organizations || []).length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4} className="text-center text-muted-foreground">
                                                No charges in this period
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                Showing a single organization.{' '}
                                <button className="underline" onClick={() => setOrganizationId('all')}>
                                    Back to all organizations
                                </button>
                            </p>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Ledger drill-down */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Recent charges</CardTitle>
                    <Select
                        value={chargesMeter}
                        onValueChange={(next) => {
                            setChargesMeter(next);
                            setChargesPage(1);
                        }}
                    >
                        <SelectTrigger className="w-56">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All meters</SelectItem>
                            {Object.keys(GROUP_LABELS).map((group) => (
                                <SelectItem key={group} value={group}>
                                    {groupLabel(group)} (group)
                                </SelectItem>
                            ))}
                            {knownMeters.map((meter) => (
                                <SelectItem key={meter} value={meter}>
                                    {meterLabel(meter)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>When</TableHead>
                                <TableHead>Organization</TableHead>
                                <TableHead>Meter</TableHead>
                                <TableHead className="text-right">Quantity</TableHead>
                                <TableHead className="text-right">COGS</TableHead>
                                <TableHead className="text-right">Credits</TableHead>
                                <TableHead className="text-right">Card</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {charges.map((charge) => (
                                <TableRow key={charge.id}>
                                    <TableCell className="whitespace-nowrap text-xs">
                                        {charge.occurred_at ? new Date(charge.occurred_at).toLocaleString() : '—'}
                                    </TableCell>
                                    <TableCell className="max-w-40 truncate">{charge.organization_name}</TableCell>
                                    <TableCell>
                                        <span className="text-xs">{meterLabel(charge.meter)}</span>
                                        {charge.metadata?.model && (
                                            <span className="ml-1 text-xs text-muted-foreground">
                                                · {charge.metadata.model}
                                            </span>
                                        )}
                                        {charge.backfilled && (
                                            <Badge variant="outline" className="ml-1 px-1 py-0 text-[10px]">
                                                backfilled
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right text-xs">
                                        {fmtQuantity(charge.quantity)} {charge.unit}
                                    </TableCell>
                                    <TableCell className="text-right text-xs">{fmtUsd(charge.cost_usd)}</TableCell>
                                    <TableCell className="text-right text-xs">{fmtCredits(charge.credits)}</TableCell>
                                    <TableCell className="text-right text-xs">v{charge.rate_card_version}</TableCell>
                                </TableRow>
                            ))}
                            {charges.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                                        No charges
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    {chargesTotalPages > 1 && (
                        <div className="mt-3 flex items-center justify-end gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={chargesPage <= 1}
                                onClick={() => setChargesPage((page) => page - 1)}
                            >
                                Previous
                            </Button>
                            <span className="text-xs text-muted-foreground">
                                Page {chargesPage} of {chargesTotalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={chargesPage >= chargesTotalPages}
                                onClick={() => setChargesPage((page) => page + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Credit plans (CREDIT_PLANS_PLAN.md §4) */}
            <CreditPlansSection />

            {/* Rate cards */}
            <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">Rate cards</CardTitle>
                    <Button size="sm" onClick={createDraft} disabled={!activeCard}>
                        New draft from active
                    </Button>
                </CardHeader>
                <CardContent className="space-y-4">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Version</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Credit peg</TableHead>
                                <TableHead>Notes</TableHead>
                                <TableHead>Activated</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {rateCards.map((card) => (
                                <TableRow key={card.id}>
                                    <TableCell className="font-medium">v{card.version}</TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={
                                                card.status === 'active'
                                                    ? 'default'
                                                    : card.status === 'draft'
                                                    ? 'secondary'
                                                    : 'outline'
                                            }
                                        >
                                            {card.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>{fmtUsd(card.credit_usd)}</TableCell>
                                    <TableCell className="max-w-64 truncate text-xs text-muted-foreground">
                                        {card.notes || '—'}
                                    </TableCell>
                                    <TableCell className="text-xs">
                                        {card.activated_at ? new Date(card.activated_at).toLocaleDateString() : '—'}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {card.status === 'draft' ? (
                                            <Button size="sm" variant="outline" onClick={() => beginEditing(card)}>
                                                Edit
                                            </Button>
                                        ) : (
                                            <Button size="sm" variant="ghost" onClick={() => beginEditing(card)}>
                                                View
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {editingCard && (
                        <div className="rounded-lg border p-4">
                            <div className="mb-3 flex items-center justify-between">
                                <div>
                                    <span className="font-medium">
                                        {editingCard.status === 'draft' ? 'Editing' : 'Viewing'} v{editingCard.version}
                                    </span>
                                    <span className="ml-2 text-xs text-muted-foreground">
                                        credits = COGS × multiplier ÷ {fmtUsd(editingCard.credit_usd)} · multiplier 0 =
                                        metered but free · floor 2.5 (60% margin)
                                    </span>
                                </div>
                                <Button size="sm" variant="ghost" onClick={() => { setEditingCardId(null); setSimulation(null); }}>
                                    Close
                                </Button>
                            </div>
                            <div className="mb-3 flex items-center gap-3">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">Default multiplier</span>
                                    <Input
                                        className="h-8 w-24"
                                        value={draftDefaultMultiplier}
                                        disabled={editingCard.status !== 'draft'}
                                        onChange={(event) => setDraftDefaultMultiplier(event.target.value)}
                                    />
                                </div>
                                <Input
                                    className="h-8 flex-1"
                                    placeholder="Notes"
                                    value={draftNotes}
                                    disabled={editingCard.status !== 'draft'}
                                    onChange={(event) => setDraftNotes(event.target.value)}
                                />
                            </div>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Meter</TableHead>
                                        <TableHead className="w-32">Multiplier</TableHead>
                                        <TableHead className="w-40">Unit COGS ($/unit)</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {Object.entries(draftMeters).map(([name, entry]) => (
                                        <TableRow key={name}>
                                            <TableCell>
                                                {meterLabel(name)}
                                                <span className="ml-2 text-xs text-muted-foreground">{name}</span>
                                                {entry.inherited && (
                                                    <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">
                                                        not on this card · {entry.inherited}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    className="h-8"
                                                    value={entry.multiplier}
                                                    disabled={editingCard.status !== 'draft'}
                                                    onChange={(event) =>
                                                        setDraftMeters((previous) => ({
                                                            ...previous,
                                                            [name]: { ...previous[name], multiplier: event.target.value },
                                                        }))
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    className="h-8"
                                                    placeholder="computed (LLM)"
                                                    value={entry.unit_cost_usd}
                                                    disabled={editingCard.status !== 'draft'}
                                                    onChange={(event) =>
                                                        setDraftMeters((previous) => ({
                                                            ...previous,
                                                            [name]: { ...previous[name], unit_cost_usd: event.target.value },
                                                        }))
                                                    }
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {editingCard.status === 'draft' && (
                                <div className="mt-3 flex items-center gap-2">
                                    <Button size="sm" variant="outline" onClick={saveDraft} disabled={savingDraft}>
                                        {savingDraft ? 'Saving…' : 'Save draft'}
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={runSimulation} disabled={simulating}>
                                        {simulating ? 'Simulating…' : 'Simulate (30d replay)'}
                                    </Button>
                                    <Button size="sm" onClick={() => activate(false)} disabled={activating}>
                                        {activating ? 'Activating…' : 'Activate'}
                                    </Button>
                                </div>
                            )}
                            {simulation && (
                                <div className="mt-4 rounded-md bg-muted/50 p-3">
                                    <p className="mb-2 text-sm font-medium">
                                        Last {simulation.days}d replayed: {fmtCredits(simulation.totals.actual_credits)} actual
                                        → {fmtCredits(simulation.totals.simulated_credits)} simulated credits (COGS{' '}
                                        {fmtUsd(simulation.totals.cost_usd)})
                                    </p>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Meter</TableHead>
                                                <TableHead className="text-right">COGS</TableHead>
                                                <TableHead className="text-right">Actual credits</TableHead>
                                                <TableHead className="text-right">Simulated</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {simulation.by_meter.map((row) => (
                                                <TableRow key={row.meter}>
                                                    <TableCell>{meterLabel(row.meter)}</TableCell>
                                                    <TableCell className="text-right">{fmtUsd(row.cost_usd)}</TableCell>
                                                    <TableCell className="text-right">{fmtCredits(row.actual_credits)}</TableCell>
                                                    <TableCell className="text-right">{fmtCredits(row.simulated_credits)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Activation confirmation (price increase / >25% change) */}
            <Dialog open={confirmChanges !== null} onOpenChange={(open) => !open && setConfirmChanges(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Confirm rate change</DialogTitle>
                        <DialogDescription>
                            This activation raises prices or changes a meter by more than 25%. Charges already written
                            keep their old pricing; everything from activation onward uses the new card.
                        </DialogDescription>
                    </DialogHeader>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Meter</TableHead>
                                <TableHead className="text-right">Multiplier</TableHead>
                                <TableHead className="text-right">Change</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(confirmChanges || []).map((change) => (
                                <TableRow key={change.meter}>
                                    <TableCell>{meterLabel(change.meter)}</TableCell>
                                    <TableCell className="text-right">
                                        {change.old_multiplier} → {change.new_multiplier}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {change.multiplier_change_pct !== undefined
                                            ? `${change.multiplier_change_pct > 0 ? '+' : ''}${change.multiplier_change_pct}%`
                                            : '—'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmChanges(null)}>
                            Cancel
                        </Button>
                        <Button onClick={() => activate(true)} disabled={activating}>
                            {activating ? 'Activating…' : 'Activate anyway'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </TabsContent>
    );
};

export default MeteringTab;
